import { ExtensionNotInstalledException } from 'shared/legacy/exceptions';
import { CvNotAvailable } from 'shared/legacy/exceptions/CvNotAvailableException';
import { LoggerBase } from './loggerBase';
import { LOG_LEVEL, LogCustomAttribute, LoggerInfoKeys, LoggerProps } from './types';
import { withLogger } from './utils';

const SUPRESS_ERRORS =
  process.env.REACT_APP_ENVIRONMENT === 'development' ? [CvNotAvailable, ExtensionNotInstalledException] : [];
const THROTTLE_ERRORS = [[ExtensionNotInstalledException, 5] as const];

export class Logger extends LoggerBase {
  private lastLogTimestamps = new Map<Error, number>();

  constructor(props: LoggerProps = {}) {
    super(props);
  }

  setCustomAttribute = (name: LogCustomAttribute, value: newrelic.SimpleType) => {
    this.safeCall(() => {
      withLogger({ debug: this.debug, level: LOG_LEVEL.INFO }, newrelic.setCustomAttribute, name, value);
    });
  };

  info = (arg: string | Record<string, any>, key?: LoggerInfoKeys) => {
    this.safeCall(() => {
      this.log(LOG_LEVEL.INFO, arg, key);
    });
  };

  error = (e: any, customAttributes?: Record<string, newrelic.SimpleType>, shouldDelay?: boolean) => {
    const error = typeof e === 'string' || e instanceof Error ? e : JSON.stringify(e);

    if (SUPRESS_ERRORS.some((ErrorType) => e instanceof ErrorType)) return;
    if (!this.shouldLogError(e)) return;

    let callback = newrelic.noticeError;

    /**
     * Weird workaround under investigation by Gabriel
     * noticeError seems behave inconsistently under our app. It always work if deleyed by 1 second, that's why this is necessary
     * I plan to fix this ASAP
     */
    if (shouldDelay) {
      callback = (err, customAttributes) => {
        newrelic.noticeError(err, customAttributes);
        setTimeout(() => newrelic.noticeError(err, { ...customAttributes, delayed: 'true' }), 1000);
      };
    }

    this.safeCall(() => {
      withLogger({ debug: this.debug, level: LOG_LEVEL.ERROR }, callback, error, customAttributes);
    });
  };

  private shouldLogError(e: any): boolean {
    const now = Date.now();

    for (const [ErrorType, intervalMinutes] of THROTTLE_ERRORS) {
      if (e instanceof ErrorType) {
        const lastLog = this.lastLogTimestamps.get(ErrorType as unknown as Error) || 0;
        const intervalMs = intervalMinutes * 60 * 1000;

        if (now - lastLog < intervalMs) {
          return false;
        }

        this.lastLogTimestamps.set(ErrorType as unknown as Error, now);
        return true;
      }
    }

    return true;
  }
}
